import { AccountType, type UserAccountInfo } from '@lub0v/santa-utils';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { isDotCom } from '@src/site';
import { telegramNotificationUrl } from '@src/utils/telegramNotifications';
import { Stack, Switch, showToast } from '@src/components';
import { santaClient } from '@src/state/client';

export interface NotificationSettingsProps {
  account: UserAccountInfo;
  onAccountChange: (account: UserAccountInfo) => void;
}

export function NotificationSettings({ account, onAccountChange }: NotificationSettingsProps) {
  const { t } = useTranslation();
  const [connectingTelegram, setConnectingTelegram] = useState(false);
  const [saving, setSaving] = useState(false);
  const notes = account.accountNotifications || [];
  const emailOn = notes.some((item) => item.accountType === AccountType.EMAIL);
  const tgNote = notes.find((item) => item.accountType === AccountType.TG);

  useEffect(() => {
    if (!connectingTelegram) return;
    let active = true;
    async function refresh() {
      if (document.visibilityState === 'hidden') return;
      try {
        const res = await santaClient.account();
        if (active && res.ok && res.data) {
          onAccountChange(res.data);
          if (res.data.accountNotifications?.some((item) => item.accountType === AccountType.TG)) {
            setConnectingTelegram(false);
          }
        }
      } catch {
        // Returning to the page should not interrupt the user on a transient error.
      }
    }
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      active = false;
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [connectingTelegram, onAccountChange]);

  async function setChannel(type: AccountType, externalId: string, enabled: boolean) {
    setSaving(true);
    try {
      const res = await santaClient.enableAccountNotification({ type, externalId, enabled });
      if (res.ok && res.data) {
        onAccountChange(res.data);
      } else {
        showToast(t('switcher.actionError'), 'error');
      }
    } catch {
      showToast(t('switcher.actionError'), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack gap={4} wide>
      <Switch
        disabled={saving}
        checked={emailOn}
        onCheckedChange={(checked) => {
          void setChannel(AccountType.EMAIL, account.email, checked);
        }}
        label={t('account.notificationsEmail')}
        hint={t('account.notificationsEmailHint')}
        testId="account-notify-email"
      />
      <Switch
        disabled={saving}
        checked={Boolean(tgNote)}
        onCheckedChange={(checked) => {
          if (!checked && tgNote) {
            void setChannel(AccountType.TG, tgNote.externalId, false);
            return;
          }
          const url = telegramNotificationUrl({
            verificationKey: account.verificationKey,
            botName: window.TelegramBotName,
            dotCom: isDotCom(),
            development: import.meta.env.DEV,
          });
          if (!url) {
            showToast(t('switcher.actionError'), 'error');
            return;
          }
          setConnectingTelegram(true);
          window.open(url, '_blank', 'noopener,noreferrer');
        }}
        label={t('account.notificationsTelegram')}
        hint={t(
          connectingTelegram
            ? 'account.notificationsTelegramConnecting'
            : 'account.notificationsTelegramHint',
        )}
        testId="account-notify-telegram"
      />
    </Stack>
  );
}
